// CC-63 — Flat-rendered Drive tree view (iterative DFS to avoid recursive JSX).
import { useMemo, useState } from "react";
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FileSpreadsheet,
  FileText,
  FileImage,
  File as FileIcon,
  ExternalLink,
  Download,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  formatBytes,
  exportFormatsFor,
  knowledgeApi,
} from "@/lib/knowledgeApi";

function iconFor(item) {
  const mime = item.mime_type || "";
  if (item.is_folder) return null;
  if (
    mime === "application/vnd.google-apps.spreadsheet" ||
    mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    mime === "application/vnd.ms-excel"
  ) {
    return <FileSpreadsheet className="h-4 w-4 text-emerald-400 shrink-0" />;
  }
  if (mime === "application/pdf") {
    return <FileText className="h-4 w-4 text-red-400 shrink-0" />;
  }
  if (
    mime === "application/vnd.google-apps.document" ||
    mime.includes("wordprocessingml")
  ) {
    return <FileText className="h-4 w-4 text-sky-400 shrink-0" />;
  }
  if (mime.startsWith("image/")) {
    return <FileImage className="h-4 w-4 text-fuchsia-400 shrink-0" />;
  }
  return <FileIcon className="h-4 w-4 text-hm-fg-subtle shrink-0" />;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return "";
  }
}

// Iterative DFS → flat list of visible rows respecting the `expanded` set.
function flatten(root, expanded) {
  if (!root) return [];
  const out = [];
  const stack = [{ node: root, depth: 0 }];
  while (stack.length) {
    const { node, depth } = stack.pop();
    out.push({ node, depth });
    if (node.is_folder && expanded.has(node.drive_id)) {
      const kids = node.children || [];
      // push in reverse so iteration remains top-to-bottom
      for (let i = kids.length - 1; i >= 0; i--) {
        stack.push({ node: kids[i], depth: depth + 1 });
      }
    }
  }
  return out;
}

function DownloadButton({ item }) {
  const formats = exportFormatsFor(item.mime_type);
  if (!formats) {
    return (
      <a
        href={knowledgeApi.downloadUrl(item.drive_id)}
        data-testid={`knowledge-download-${item.drive_id}`}
        title="Download"
        className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong"
      >
        <Download className="h-3.5 w-3.5" />
      </a>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid={`knowledge-download-menu-${item.drive_id}`}
          className="h-8 text-xs border-hm-border text-hm-fg-muted hover:text-hm-fg"
        >
          <Download className="h-3.5 w-3.5 mr-1.5" />
          Download
          <ChevronDown className="h-3 w-3 ml-1 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px]">
        {formats.map(({ format, label }) => (
          <DropdownMenuItem key={format} asChild>
            <a
              href={knowledgeApi.downloadUrl(item.drive_id, format)}
              data-testid={`knowledge-download-${item.drive_id}-${format}`}
              className="cursor-pointer"
            >
              {label}
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function KnowledgeTree({ root }) {
  const [expanded, setExpanded] = useState(() =>
    new Set(root?.drive_id ? [root.drive_id] : []),
  );

  function toggle(id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const rows = useMemo(() => flatten(root, expanded), [root, expanded]);
  if (!root) return null;

  return (
    <div data-testid="knowledge-tree" className="divide-y divide-hm-border/50">
      {rows.map(({ node, depth }) => {
        const isFolder = node.is_folder;
        const isOpen = expanded.has(node.drive_id);
        const childCount = (node.children || []).length;
        return (
          <div
            key={node.drive_id}
            data-testid={`knowledge-node-${node.drive_id}`}
            className="group flex items-center gap-2 px-3 py-2 hover:bg-hm-bg-1 transition-colors"
            style={{ paddingLeft: `${12 + depth * 20}px` }}
          >
            {isFolder ? (
              <button
                type="button"
                data-testid={`knowledge-toggle-${node.drive_id}`}
                onClick={() => toggle(node.drive_id)}
                className="p-0.5 rounded hover:bg-hm-bg-2 text-hm-fg-muted"
                aria-label={isOpen ? "Collapse" : "Expand"}
              >
                {isOpen
                  ? <ChevronDown className="h-4 w-4" />
                  : <ChevronRight className="h-4 w-4" />}
              </button>
            ) : (
              <span className="w-5 shrink-0" />
            )}

            {isFolder
              ? (isOpen
                  ? <FolderOpen className="h-4 w-4 text-hm-accent shrink-0" />
                  : <Folder className="h-4 w-4 text-hm-accent shrink-0" />)
              : iconFor(node)}

            <div className="min-w-0 flex-1 flex items-center gap-3">
              <span className="truncate text-sm text-hm-fg">{node.name}</span>
              {!isFolder && (
                <span className="hidden sm:inline mono text-[11px] text-hm-fg-subtle whitespace-nowrap">
                  {formatBytes(node.size_bytes)}
                  {node.drive_modified_time
                    ? ` · ${formatDate(node.drive_modified_time)}`
                    : ""}
                </span>
              )}
              {isFolder && (
                <span className="mono text-[11px] text-hm-fg-subtle whitespace-nowrap">
                  {childCount} item{childCount === 1 ? "" : "s"}
                </span>
              )}
            </div>

            {!isFolder && (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <DownloadButton item={node} />
                {node.drive_url && (
                  <a
                    href={node.drive_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`knowledge-open-drive-${node.drive_id}`}
                    title="Open in Google Drive"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
